import type { HouseConfig } from '@/model/schema';

/**
 * Candidate scoring (0..1). Weighted blend of the dimensions listed in
 * design doc §"Recommended scoring dimensions". A scoring phase is critical:
 * a validity-only generator produces legal-but-ugly results (see Pitfall 1).
 *
 * Each dimension returns a score in [0,1]. The final score is the weighted
 * sum divided by the total weight, so adding new dimensions doesn't re-tune
 * thresholds elsewhere.
 */

export interface ScoreBreakdown {
  total: number;
  dimensions: Array<{ name: string; score: number; weight: number }>;
}

const W = {
  footprintProportion: 1.2,
  storyProportion: 1.0,
  facadeRhythm: 1.2,
  verticalAlignment: 1.0,
  frontDoorProminence: 1.0,
  roofCompatibility: 1.3,
  fenceCompatibility: 0.5,
  interiorPlausibility: 0.8,
  narrowSpaceAvoidance: 0.9,
  visualBalance: 1.0,
} as const;

export function scoreCandidate(cfg: HouseConfig): ScoreBreakdown {
  const dims: ScoreBreakdown['dimensions'] = [];

  dims.push({
    name: 'footprintProportion',
    score: scoreFootprintProportion(cfg),
    weight: W.footprintProportion,
  });
  dims.push({
    name: 'storyProportion',
    score: scoreStoryProportion(cfg),
    weight: W.storyProportion,
  });
  dims.push({
    name: 'facadeRhythm',
    score: scoreFacadeRhythm(cfg),
    weight: W.facadeRhythm,
  });
  dims.push({
    name: 'verticalAlignment',
    score: scoreVerticalAlignment(cfg),
    weight: W.verticalAlignment,
  });
  dims.push({
    name: 'frontDoorProminence',
    score: scoreFrontDoorProminence(cfg),
    weight: W.frontDoorProminence,
  });
  dims.push({
    name: 'roofCompatibility',
    score: scoreRoofCompatibility(cfg),
    weight: W.roofCompatibility,
  });
  dims.push({
    name: 'fenceCompatibility',
    score: scoreFenceCompatibility(cfg),
    weight: W.fenceCompatibility,
  });
  dims.push({
    name: 'interiorPlausibility',
    score: scoreInteriorPlausibility(cfg),
    weight: W.interiorPlausibility,
  });
  dims.push({
    name: 'narrowSpaceAvoidance',
    score: scoreNarrowSpaceAvoidance(cfg),
    weight: W.narrowSpaceAvoidance,
  });
  dims.push({
    name: 'visualBalance',
    score: scoreVisualBalance(cfg),
    weight: W.visualBalance,
  });

  let totalW = 0;
  let totalS = 0;
  for (const d of dims) {
    totalW += d.weight;
    totalS += d.score * d.weight;
  }
  return { total: totalS / totalW, dimensions: dims };
}

function bell(value: number, target: number, tolerance: number): number {
  // 1 at target, falling off smoothly to ~0 at ±tolerance*2.
  const d = (value - target) / tolerance;
  return Math.max(0, 1 - d * d * 0.5);
}

function scoreFootprintProportion(cfg: HouseConfig): number {
  const aspect = cfg.house.footprintWidth / cfg.house.footprintDepth;
  // Prefer aspect in [0.9, 1.7], sweet spot near 1.3.
  return bell(aspect, 1.3, 0.6);
}

function scoreStoryProportion(cfg: HouseConfig): number {
  const stories = cfg.stories.length;
  const shortSide = Math.min(cfg.house.footprintWidth, cfg.house.footprintDepth);
  // Story count shouldn't be wildly out of scale with the short side.
  if (stories === 1) return 1;
  if (stories === 2) return shortSide >= 6 ? 1 : 0.5;
  if (stories === 3) return shortSide >= 7 ? 1 : 0.4;
  return 0.2;
}

function scoreFacadeRhythm(cfg: HouseConfig): number {
  // Reward evenly spaced front-facade windows.
  const front = cfg.stories[0]?.windows.filter((w) => w.side === 'front') ?? [];
  if (front.length < 2) return 0.7;
  const gaps: number[] = [];
  const sorted = [...front].sort((a, b) => a.offset - b.offset);
  for (let i = 1; i < sorted.length; i++) {
    gaps.push(sorted[i]!.offset - sorted[i - 1]!.offset);
  }
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance =
    gaps.reduce((a, b) => a + (b - mean) ** 2, 0) / gaps.length;
  const stdev = Math.sqrt(variance);
  // Normalize by facade length; small relative stdev = strong rhythm.
  const rel = stdev / Math.max(1, cfg.house.footprintWidth);
  return Math.max(0, 1 - rel * 4);
}

function scoreVerticalAlignment(cfg: HouseConfig): number {
  if (cfg.stories.length < 2) return 1;
  let matched = 0;
  let total = 0;
  const tol = 0.4;
  for (let i = 1; i < cfg.stories.length; i++) {
    const upper = cfg.stories[i]!;
    const lower = cfg.stories[0]!;
    for (const uw of upper.windows) {
      if (uw.side !== 'front') continue;
      total++;
      const hit = lower.windows.some(
        (lw) => lw.side === 'front' && Math.abs(lw.offset - uw.offset) < tol,
      );
      if (hit) matched++;
    }
  }
  if (total === 0) return 0.8;
  return matched / total;
}

function scoreFrontDoorProminence(cfg: HouseConfig): number {
  const door = cfg.stories[0]?.doors.find((d) => d.role === 'front');
  if (!door) return 0;
  const facadeW = cfg.house.footprintWidth;
  const centerDist = Math.abs(door.offset + door.width / 2 - facadeW / 2);
  return Math.max(0, 1 - centerDist / (facadeW / 2));
}

function scoreRoofCompatibility(cfg: HouseConfig): number {
  const aspect = cfg.house.footprintWidth / cfg.house.footprintDepth;
  switch (cfg.roof.type) {
    case 'flat':
      return 0.9;
    case 'gable':
      // Likes clear longer axis: reward aspect further from 1.0.
      return Math.min(1, 0.4 + Math.abs(aspect - 1) * 1.2);
    case 'hip':
      // Likes balanced: reward aspect close to 1.0.
      return bell(aspect, 1.0, 0.5);
  }
}

function scoreFenceCompatibility(cfg: HouseConfig): number {
  if (!cfg.fence) return 1;
  const lotArea = cfg.lot.width * cfg.lot.depth;
  if (lotArea < 100) return 0.3;
  return 1;
}

function scoreInteriorPlausibility(cfg: HouseConfig): number {
  // Rough check: stories have at least one hall-or-stairs and no rooms below 2m on a side.
  let ok = 0;
  let total = 0;
  for (const s of cfg.stories) {
    total++;
    const hasCirc = s.rooms.some((r) => r.type === 'hall' || r.type === 'stairs');
    const noSlits = s.rooms.every((r) => r.width >= 2.1 && r.depth >= 2.1);
    if (hasCirc && noSlits) ok++;
  }
  return ok / Math.max(1, total);
}

function scoreNarrowSpaceAvoidance(cfg: HouseConfig): number {
  let bad = 0;
  let total = 0;
  for (const s of cfg.stories) {
    for (const r of s.rooms) {
      total++;
      const aspect = Math.max(r.width, r.depth) / Math.min(r.width, r.depth);
      if (aspect > 3) bad++;
    }
  }
  if (total === 0) return 1;
  return 1 - bad / total;
}

function scoreVisualBalance(cfg: HouseConfig): number {
  // Cheap proxy: the centroid of front-facade openings should sit near the
  // facade midpoint.
  const front = cfg.stories[0]?.windows.filter((w) => w.side === 'front') ?? [];
  const door = cfg.stories[0]?.doors.find((d) => d.role === 'front');
  const facadeW = cfg.house.footprintWidth;
  const items: Array<{ center: number; weight: number }> = [];
  for (const w of front) items.push({ center: w.offset + w.width / 2, weight: w.width });
  if (door) items.push({ center: door.offset + door.width / 2, weight: door.width * 1.5 });
  if (items.length === 0) return 0.5;
  const totalW = items.reduce((a, b) => a + b.weight, 0);
  const centroid = items.reduce((a, b) => a + b.center * b.weight, 0) / totalW;
  const offsetFromMid = Math.abs(centroid - facadeW / 2);
  return Math.max(0, 1 - offsetFromMid / (facadeW / 2));
}
